// src/components/common/Layout/Breadcrumb.tsx
import {Link} from "react-router-dom";
import {ChevronRightIcon, HomeIcon} from "@heroicons/react/24/outline";
import {clsx} from "clsx";

interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  className = "",
  showHome = true,
}) => {
  return (
    <nav className={clsx("flex", className)} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2 text-sm">
        {/* Home */}
        {showHome && (
          <li>
            <Link
              to="/dashboard"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Go to dashboard"
            >
              <HomeIcon className="w-4 h-4" />
            </Link>
          </li>
        )}

        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isCurrent = item.current || isLast;

          return (
            <li key={index} className="flex items-center">
              {/* Separator */}
              {(showHome || index > 0) && (
                <ChevronRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 mx-2 flex-shrink-0" />
              )}

              {/* Breadcrumb item */}
              {item.href && !isCurrent ? (
                <Link
                  to={item.href}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors truncate"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={clsx(
                    "truncate",
                    isCurrent
                      ? "text-gray-900 dark:text-white font-medium"
                      : "text-gray-500 dark:text-gray-400"
                  )}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
